import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { User, AlertTriangle, ChevronRight, ArrowUpDown, Bell, UserPlus, CheckCircle2, XCircle, ChevronDown } from 'lucide-react';
import {
  SectionHeading, KPICard, Skeleton, ICON_PX, FilterToggle, BriefCard,
  Tooltip, TooltipTrigger, TooltipContent, StatusBadge, DomainDot,
} from '../ui';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuLabel,
} from '../ui/dropdown-menu';
import { usePracticeManagement } from '../../contexts/PracticeManagementContext';
import CompletionModal from '../shared/CompletionModal';
import SkipReasonModal from '../shared/SkipReasonModal';

const DOMAIN_LABEL = {
  quality: 'Quality', staffing: 'Staffing', access: 'Access', finance: 'Finance',
  compliance: 'Compliance', safety: 'Safety', operations: 'Operations', communications: 'Comms',
};

function PMOverviewTab({ readOnly = false, navigateTo }) {
  const { overviewMetrics, checklists, orphanedTasks, loading, staffRoster, completeTask, skipTask, delegateTask } = usePracticeManagement();

  // ── Local filter state ──
  const [taskFilter, setTaskFilter] = useState(null);   // null | 'completed' | 'pending' | 'inProgress' | 'overdue'
  const [wfFilter, setWfFilter] = useState(null);        // null | 'onTrack' | 'atRisk' | 'completed'
  const [ownerSortCol, setOwnerSortCol] = useState('role');
  const [ownerSortAsc, setOwnerSortAsc] = useState(true);
  const [ownerPeriod, setOwnerPeriod] = useState('today');    // 'today' | 'thisWeek' | 'thisMonth'
  const [ownerCadence, setOwnerCadence] = useState(null);      // null (all) | 'daily' | 'weekly' | 'monthly'

  // ── Ownership accordion expand state ──
  const [expandedOwner, setExpandedOwner] = useState(null);   // { rowIdx, col } or null
  const [completeOpen, setCompleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [modalTask, setModalTask] = useState(null);
  const expandRef = useRef(null);

  // ── P3: AI Insights (computed from data) ──
  const insights = useMemo(() => {
    const items = [];

    // Orphaned role alert
    if (orphanedTasks.length > 0) {
      const roles = [...new Set(orphanedTasks.map((t) => t.ownerRole.replace(/_/g, ' ')))];
      items.push({
        id: 'orphaned',
        type: 'alert',
        text: `${roles.join(', ')} role${roles.length > 1 ? 's' : ''} unassigned — ${orphanedTasks.length} task${orphanedTasks.length > 1 ? 's' : ''} need coverage`,
        action: { label: 'Resolve Now', tab: 'queue', filter: { status: 'orphaned' } },
      });
    }

    // Overdue compliance workflows
    if (checklists) {
      const overdueWfs = checklists.filter((cl) => {
        const pct = cl.stepsTotal > 0 ? cl.stepsCompleted / cl.stepsTotal : 1;
        return pct < 1 && cl.regulatorySource;
      });
      overdueWfs.forEach((wf) => {
        const pct = Math.round((wf.stepsCompleted / wf.stepsTotal) * 100);
        if (pct < 50) {
          items.push({
            id: `wf-${wf.id}`,
            type: 'alert',
            text: `${wf.name} only ${pct}% complete — regulatory compliance at risk`,
            action: { label: 'View', tab: 'checklists', filter: { workflowId: wf.id } },
          });
        }
      });
    }

    // Chronic skip detection
    if (overviewMetrics?.ownershipSummary) {
      overviewMetrics.ownershipSummary.forEach((o) => {
        if (o.overdue > 2) {
          items.push({
            id: `skip-${o.role}`,
            type: 'insight',
            text: `${o.individual || o.role.replace(/_/g, ' ')} has ${o.overdue} overdue tasks. Consider redistributing workload or investigating barriers.`,
            action: { label: 'View Tasks', tab: 'queue', filter: { owner: o.individual } },
          });
        }
      });
    }

    // Capacity signal
    if (overviewMetrics?.tasksToday) {
      const t = overviewMetrics.tasksToday;
      if (t.total > 15 && t.overdue > 2) {
        items.push({
          id: 'capacity',
          type: 'insight',
          text: `${t.total} tasks today with ${t.overdue} already overdue. Task load may exceed available capacity.`,
          action: { label: 'View Queue', tab: 'queue' },
        });
      }
    }

    return items;
  }, [orphanedTasks, checklists, overviewMetrics]);

  // ── P4: Morning Brief ──
  const morningBrief = useMemo(() => {
    if (!overviewMetrics) return null;
    const m = overviewMetrics;
    const parts = [];
    parts.push(`You have ${m.tasksToday.total} tasks today`);
    if (m.tasksToday.overdue > 0) parts.push(`${m.tasksToday.overdue} are already overdue`);
    if (orphanedTasks.length > 0) parts.push(`${orphanedTasks.length} task${orphanedTasks.length > 1 ? 's' : ''} need coverage due to unassigned roles`);

    const activeWfs = (checklists || []).filter((cl) => cl.stepsCompleted < cl.stepsTotal);
    if (activeWfs.length > 0) parts.push(`${activeWfs.length} workflow${activeWfs.length > 1 ? 's' : ''} in progress`);

    return parts.join('. ') + '.';
  }, [overviewMetrics, orphanedTasks, checklists]);

  // ── Workflow data with at-risk calculation ──
  const workflowData = useMemo(() => {
    if (!checklists) return { all: [], onTrack: [], atRisk: [], completed: [] };

    const now = new Date();
    const isAtRisk = (wf) => {
      if (wf.stepsCompleted === wf.stepsTotal) return false;
      if (!wf.dueDate) return false;
      const due = new Date(wf.dueDate);
      const minutesRemaining = Math.max(0, (due - now) / 60000);
      const stepsLeft = wf.stepsTotal - wf.stepsCompleted;
      const minutesPerStep = wf.estimatedMinutesPerStep || 10;
      return stepsLeft * minutesPerStep > minutesRemaining;
    };

    const enriched = checklists.map((wf) => {
      const pct = wf.stepsTotal > 0 ? Math.round((wf.stepsCompleted / wf.stepsTotal) * 100) : 0;
      const done = wf.stepsCompleted === wf.stepsTotal;
      const risk = !done && isAtRisk(wf);
      return { ...wf, pct, status: done ? 'completed' : risk ? 'atRisk' : 'onTrack' };
    });

    return {
      all: enriched,
      onTrack: enriched.filter((w) => w.status === 'onTrack'),
      atRisk: enriched.filter((w) => w.status === 'atRisk'),
      completed: enriched.filter((w) => w.status === 'completed'),
    };
  }, [checklists]);

  // ── Filtered domain breakdown ──
  const filteredDomains = useMemo(() => {
    if (!overviewMetrics) return [];
    return overviewMetrics.domainBreakdown.map((d) => {
      if (!taskFilter) return { ...d, dimmed: false };
      // Map filter key to the domain's count for that status
      const countMap = {
        completed: d.completedCount ?? 0,
        pending: d.pendingCount ?? 0,
        inProgress: d.inProgressCount ?? 0,
        overdue: d.overdueCount ?? 0,
      };
      const count = countMap[taskFilter] ?? 0;
      return { ...d, filteredCount: count, dimmed: count === 0 };
    });
  }, [overviewMetrics, taskFilter]);

  // ── Filtered workflow list ──
  const filteredWorkflows = useMemo(() => {
    if (!wfFilter) return workflowData.all;
    return workflowData[wfFilter] || workflowData.all;
  }, [workflowData, wfFilter]);

  // ── Sorted ownership summary (filtered by period + cadence) ──
  const sortedOwnership = useMemo(() => {
    if (!overviewMetrics) return [];
    const cadences = ownerCadence ? [ownerCadence] : ['daily', 'weekly', 'monthly'];

    const rows = overviewMetrics.ownershipSummary.map((o) => {
      const periodData = o.byPeriod?.[ownerPeriod];
      if (!periodData) return { ...o, tasksAssigned: 0, completed: 0, overdue: 0 };
      let assigned = 0, completed = 0, overdue = 0;
      cadences.forEach((c) => {
        const d = periodData[c];
        if (d) { assigned += d.assigned; completed += d.completed; overdue += d.overdue; }
      });
      return { ...o, tasksAssigned: assigned, completed, overdue };
    });

    const dir = ownerSortAsc ? 1 : -1;
    rows.sort((a, b) => {
      switch (ownerSortCol) {
        case 'role': return dir * a.role.localeCompare(b.role);
        case 'individual': return dir * (a.individual || '').localeCompare(b.individual || '');
        case 'assigned': return dir * (a.tasksAssigned - b.tasksAssigned);
        case 'done': return dir * (a.completed - b.completed);
        case 'overdue': return dir * (a.overdue - b.overdue);
        default: return 0;
      }
    });
    return rows;
  }, [overviewMetrics, ownerSortCol, ownerSortAsc, ownerPeriod, ownerCadence]);

  // ── Ownership accordion handlers (must be before early returns) ──
  const handleCellClick = useCallback((rowIdx, col) => {
    setExpandedOwner((prev) => {
      if (prev && prev.rowIdx === rowIdx && prev.col === col) return null;
      return { rowIdx, col };
    });
  }, []);

  const getReassignOptions = useCallback((role) => {
    if (!staffRoster) return [];
    return staffRoster
      .filter((s) => s.role === role && s.available)
      .map((s) => ({ value: s.id, label: s.name }));
  }, [staffRoster]);

  const getExpandedTasks = useCallback((owner, col) => {
    if (!owner.tasks) return [];
    const statusMap = { assigned: null, done: 'completed', overdue: 'overdue' };
    const filterStatus = statusMap[col];
    if (filterStatus === null) return owner.tasks;
    return owner.tasks.filter((t) => t.status === filterStatus);
  }, []);

  const handleSendEmail = useCallback((task, individual) => {
    const subject = encodeURIComponent(`Reminder: ${task.taskName} due ${task.dueDate}`);
    const body = encodeURIComponent(`Hi ${individual || 'Team'},\n\nThis is a reminder that "${task.taskName}" is due on ${task.dueDate} at ${task.dueTime}.\n\nPlease complete at your earliest convenience.\n\nThank you.`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
  }, []);

  const handleMarkComplete = useCallback((task) => { setModalTask(task); setCompleteOpen(true); }, []);
  const handleCancel = useCallback((task) => { setModalTask(task); setCancelOpen(true); }, []);
  const handleReassign = useCallback((taskId, newUserId) => { delegateTask?.(taskId, newUserId); }, [delegateTask]);
  const onCompleteConfirm = useCallback((taskId, data) => { completeTask?.(taskId, data); setCompleteOpen(false); setModalTask(null); }, [completeTask]);
  const onCancelConfirm = useCallback((taskId, reason) => { skipTask?.(taskId, reason); setCancelOpen(false); setModalTask(null); }, [skipTask]);

  // Escape to collapse
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') { setExpandedOwner(null); document.activeElement?.blur(); } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Scroll expanded row into view
  useEffect(() => {
    if (expandedOwner && expandRef.current) {
      expandRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [expandedOwner]);

  if (loading) {
    return (
      <div className="space-y-3 py-3">
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((i) => <Skeleton.KPICard key={i} size="sm" />)}
        </div>
      </div>
    );
  }

  if (!overviewMetrics) return null;
  const m = overviewMetrics;

  const handleOwnerSort = (col) => {
    if (ownerSortCol === col) setOwnerSortAsc((prev) => !prev);
    else { setOwnerSortCol(col); setOwnerSortAsc(true); }
  };

  // Click handler wrapper
  const go = (tab, filter) => navigateTo?.(tab, filter);

  // Toggle filter: click to engage, click again to disengage
  const toggleTaskFilter = (key) => setTaskFilter((prev) => (prev === key ? null : key));
  const toggleWfFilter = (key) => setWfFilter((prev) => (prev === key ? null : key));

  // Task status filter items
  const taskStatuses = [
    { key: 'completed', label: 'Completed', value: m.tasksToday.completed, color: 'text-green-600', bg: 'bg-green-500', activeBg: 'bg-green-50 ring-1 ring-green-300' },
    { key: 'pending', label: 'Pending', value: m.tasksToday.pending, color: 'text-text-secondary', bg: 'bg-gray-400', activeBg: 'bg-gray-50 ring-1 ring-gray-300' },
    { key: 'inProgress', label: 'In Progress', value: m.tasksToday.inProgress, color: 'text-aqua-dark', bg: 'bg-aqua', activeBg: 'bg-aqua-glow/10 ring-1 ring-aqua' },
    { key: 'overdue', label: 'Overdue', value: m.tasksToday.overdue, color: 'text-red-600', bg: 'bg-red-400', activeBg: 'bg-red-50 ring-1 ring-red-300' },
  ];

  // Workflow status filter items
  const wfStatuses = [
    { key: 'onTrack', label: 'On Track', value: workflowData.onTrack.length, color: 'text-green-nn', activeBg: 'bg-green-50 ring-1 ring-green-300' },
    { key: 'atRisk', label: 'At Risk', value: workflowData.atRisk.length, color: 'text-red-critical', activeBg: 'bg-red-50 ring-1 ring-red-300' },
    { key: 'completed', label: 'Completed', value: workflowData.completed.length, color: 'text-text-muted', activeBg: 'bg-gray-50 ring-1 ring-gray-300' },
  ];

  const wfBarColor = (status) => {
    if (status === 'completed') return 'bg-green-nn';
    if (status === 'atRisk') return 'bg-red-critical';
    return 'bg-aqua';
  };

  return (
    <div className="space-y-3 py-3 outline-none" tabIndex={-1}>
      {/* ── Combined Brief + Alerts + Insights Card ── */}
      <BriefCard
        narrative={morningBrief}
        alerts={insights.filter((i) => i.type === 'alert').map((a) => ({
          id: a.id,
          text: a.text,
          onClick: () => a.action && go(a.action.tab, a.action.filter),
        }))}
        insights={insights.filter((i) => i.type === 'insight').map((ins) => ({
          id: ins.id,
          text: ins.text,
          onClick: () => ins.action && go(ins.action.tab, ins.action.filter),
        }))}
      />

      {/* ── P1: Clickable KPI Row ── */}
      <SectionHeading
        size="sm"
        title="Key Indicators"
        className="mb-1.5"
        info="Key operational metrics for your practice's task management health this week. Click any card to drill down."
      />
      <div className="grid grid-cols-4 gap-2">
        <div className="cursor-pointer" onClick={() => go('reporting', { view: 'completed' })}>
          <KPICard
            label="Completion Rate"
            value={`${m.completionRate.value}%`}
            trend={m.completionRate.trendLabel}
            trendDown={m.completionRate.trend < 0}
            domain="practice"
            entityId="practice.completionRate"
            size="sm"
            density="compact"
            accentPlacement="top"
            alertLevel={m.completionRate.value < 80 ? 'critical' : m.completionRate.value < 90 ? 'warning' : 'none'}
          />
        </div>
        <div className="cursor-pointer" onClick={() => go('queue', { status: 'overdue' })}>
          <KPICard
            label="Overdue Tasks"
            value={String(m.overdueCount.value)}
            trend={m.overdueCount.trendLabel}
            trendDown={m.overdueCount.trend > 0}
            domain="practice"
            entityId="practice.overdueTasks"
            size="sm"
            density="compact"
            accentPlacement="top"
            alertLevel={m.overdueCount.value > 5 ? 'critical' : m.overdueCount.value > 2 ? 'warning' : 'none'}
          />
        </div>
        <div className="cursor-pointer" onClick={() => go('reporting', { view: 'skipped' })}>
          <KPICard
            label="Skip Rate"
            value={`${m.skipRate.value}%`}
            trend={m.skipRate.trendLabel}
            trendDown={m.skipRate.trend > 0}
            domain="practice"
            entityId="practice.skipRate"
            size="sm"
            density="compact"
            accentPlacement="top"
            alertLevel={m.skipRate.value > 15 ? 'critical' : m.skipRate.value > 10 ? 'warning' : 'none'}
          />
        </div>
        <div className="cursor-pointer" onClick={() => go('reporting')}>
          <KPICard
            label="Avg Completion"
            value={`${m.avgCompletionTime.value} min`}
            trend={m.avgCompletionTime.trendLabel}
            trendDown={m.avgCompletionTime.trend > 0}
            domain="practice"
            entityId="practice.avgCompletion"
            size="sm"
            density="compact"
            accentPlacement="top"
            alertLevel="none"
          />
        </div>
      </div>

      {/* ── Task Summary (left) + Workflow Summary (right) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* LEFT: Task Summary */}
        <div>
          <SectionHeading size="sm" title="Task Summary" className="mb-1.5" info="Status breakdown of today's tasks. Click a status to filter the domain breakdown below. Click again to clear." />
          <div className="rounded-lg border border-nn-border bg-nn-white p-3">
            {/* Status filter counts */}
            <div className="flex items-baseline gap-1">
              <span className="text-[22px] font-bold text-text-dark mr-1">{m.tasksToday.total}</span>
              <span className="text-caption text-text-muted mr-3">Total</span>
              {taskStatuses.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => toggleTaskFilter(s.key)}
                  className={`rounded-md px-2 py-1 text-center cursor-pointer transition-all ${
                    taskFilter === s.key ? s.activeBg : 'hover:bg-nn-bg-subtle'
                  }`}
                >
                  <span className={`text-base font-bold ${s.color}`}>{s.value}</span>
                  <span className="text-caption text-text-muted ml-1">{s.label}</span>
                </button>
              ))}
            </div>

            {/* Domain Breakdown (filtered) */}
            <div className="mt-3 pt-3 border-t border-nn-border divide-y divide-nn-border">
              {filteredDomains.map((d) => (
                <div
                  key={d.domain}
                  className={`flex items-center gap-3 px-1 py-1.5 cursor-pointer hover:bg-nn-bg-subtle transition-all ${
                    d.dimmed ? 'opacity-30' : ''
                  }`}
                  onClick={() => go('queue', { domain: d.domain, ...(taskFilter ? { status: taskFilter } : {}) })}
                >
                  <span className="text-body-sm font-medium text-text-dark w-20 truncate">{DOMAIN_LABEL[d.domain] || d.domain}</span>
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${d.completionRate >= 90 ? 'bg-green-500' : d.completionRate >= 80 ? 'bg-yellow-400' : 'bg-red-400'}`}
                      style={{ width: `${d.completionRate}%` }}
                    />
                  </div>
                  {taskFilter && d.filteredCount !== undefined ? (
                    <span className="text-body-sm text-text-secondary w-6 text-right">{d.filteredCount}</span>
                  ) : (
                    <span className="text-body-sm text-text-secondary w-10 text-right">{d.completionRate}%</span>
                  )}
                  {!taskFilter && d.overdueCount > 0 && (
                    <span className="text-body-sm text-red-600 flex items-center gap-0.5">
                      <AlertTriangle size={ICON_PX.xs} /> {d.overdueCount}
                    </span>
                  )}
                  <ChevronRight size={ICON_PX.xs} className="text-text-muted shrink-0" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: Workflow Summary */}
        <div className="flex flex-col">
          <SectionHeading size="sm" title="Workflow Summary" className="mb-1.5" info="All workflows by status. Click a status to filter. At-risk = remaining task time exceeds time to due date." />
          <div className="rounded-lg border border-nn-border bg-nn-white p-3 flex flex-col flex-1 min-h-0">
            {/* Status filter counts */}
            <div className="flex items-center gap-2">
              <span className="text-[22px] font-bold text-text-dark mr-1">{workflowData.all.length}</span>
              <span className="text-caption text-text-muted mr-2">Total</span>
              {wfStatuses.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => toggleWfFilter(s.key)}
                  className={`rounded-md px-2 py-1 cursor-pointer transition-all ${
                    wfFilter === s.key ? s.activeBg : 'hover:bg-nn-bg-subtle'
                  }`}
                >
                  <span className={`text-base font-bold ${s.color}`}>{s.value}</span>
                  <span className="text-caption text-text-muted ml-1">{s.label}</span>
                </button>
              ))}
            </div>

            {/* Scrollable workflow list */}
            <div className="mt-3 pt-3 border-t border-nn-border overflow-y-auto flex-1 -mr-3 pr-1" style={{ maxHeight: '280px' }}>
              <div className="divide-y divide-nn-border">
                {filteredWorkflows.map((wf) => (
                  <div
                    key={wf.id}
                    className="flex items-center gap-2 px-1 py-2.5 cursor-pointer hover:bg-nn-bg-subtle transition-colors"
                    onClick={() => go('checklists', { workflowId: wf.id })}
                  >
                    {wf.status === 'atRisk' && <AlertTriangle size={ICON_PX.xs} className="text-red-critical shrink-0" />}
                    <span className="text-body-sm text-text-dark flex-1 truncate">{wf.name}</span>
                    <span className="text-caption font-medium text-text-muted shrink-0 w-8 text-right">
                      {wf.pct}%
                    </span>
                    <div className="w-20 h-1.5 bg-nn-border rounded-full overflow-hidden shrink-0">
                      <div
                        className={`h-full rounded-full ${wfBarColor(wf.status)}`}
                        style={{ width: `${wf.pct}%` }}
                      />
                    </div>
                    <ChevronRight size={ICON_PX.xs} className="text-text-muted shrink-0" />
                  </div>
                ))}
                {filteredWorkflows.length === 0 && (
                  <p className="text-body-sm text-text-muted text-center py-4">No workflows match this filter.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Ownership Summary (full width) ── */}
      <SectionHeading
        size="sm"
        title="Ownership Summary"
        className="mb-1.5"
        info="Who owns what tasks by period and cadence. Click a row to filter by owner."
        action={
          <div className="flex items-center gap-2">
            <FilterToggle
              value={ownerPeriod}
              onChange={setOwnerPeriod}
              options={[
                { value: 'today', label: 'Today' },
                { value: 'thisWeek', label: 'This Week' },
                { value: 'thisMonth', label: 'This Month' },
              ]}
              size="sm"
            />
            <FilterToggle
              value={ownerCadence ?? 'all'}
              onChange={(v) => setOwnerCadence(v === 'all' ? null : v)}
              options={[
                { value: 'all', label: 'All' },
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
              ]}
              size="sm"
            />
          </div>
        }
      />
      <div className="rounded-lg border border-nn-border bg-nn-white shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-body table-fixed">
            <colgroup>
              <col className="w-[20%]" />
              <col className="w-[30%]" />
              <col className="w-[16%]" />
              <col className="w-[17%]" />
              <col className="w-[17%]" />
            </colgroup>
            <thead>
              <tr className="border-b border-nn-border">
                {[
                  { key: 'role', label: 'Role', align: 'text-left' },
                  { key: 'individual', label: 'Individual', align: 'text-left' },
                  { key: 'assigned', label: 'Assigned', align: 'text-center' },
                  { key: 'done', label: 'Done', align: 'text-center' },
                  { key: 'overdue', label: 'Overdue', align: 'text-center' },
                ].map((col) => (
                  <th
                    key={col.key}
                    className={`${col.align} py-2.5 px-3 text-caption font-bold text-text-muted uppercase tracking-wider cursor-pointer select-none hover:text-text-dark transition-colors bg-slate-200`}
                    onClick={() => handleOwnerSort(col.key)}
                  >
                    <div className={`flex items-center gap-1 ${col.align === 'text-center' ? 'justify-center' : ''}`}>
                      {col.label}
                      <ArrowUpDown size={10} className={ownerSortCol === col.key ? 'text-aqua' : 'text-text-muted/50'} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedOwnership.map((o, i) => {
                const isExpanded = expandedOwner?.rowIdx === i;
                const expandCol = expandedOwner?.col;
                const expandedTasks = isExpanded ? getExpandedTasks(o, expandCol) : [];
                const colLabel = { assigned: 'assigned', done: 'completed', overdue: 'overdue' }[expandCol] || '';

                return (
                  <React.Fragment key={i}>
                    <tr
                      className={`border-b border-nn-border cursor-pointer hover:bg-surface-warm transition-colors ${o.isOrphaned ? 'bg-[var(--red-bg)]' : ''} ${isExpanded ? 'bg-surface-warm' : ''}`}
                    >
                      <td className="py-2.5 px-3 capitalize" onClick={() => go('queue', { owner: o.individual || o.role })}>{o.role.replace(/_/g, ' ')}</td>
                      <td className="py-2.5 px-3" onClick={() => go('queue', { owner: o.individual || o.role })}>
                        {o.individual ? (
                          <span className="flex items-center gap-1"><User size={ICON_PX.xs} /> {o.individual}</span>
                        ) : (
                          <span className="text-red-critical flex items-center gap-1">
                            <AlertTriangle size={ICON_PX.xs} /> Unassigned
                          </span>
                        )}
                      </td>
                      <td
                        className={`py-2.5 px-3 text-center cursor-pointer transition-colors ${isExpanded && expandCol === 'assigned' ? 'bg-aqua-glow/20 font-bold' : 'hover:bg-aqua-glow/10'}`}
                        onClick={(e) => { e.stopPropagation(); handleCellClick(i, 'assigned'); }}
                      >
                        <span className="inline-flex items-center gap-1">
                          {o.tasksAssigned}
                          {isExpanded && expandCol === 'assigned' && <ChevronDown size={ICON_PX.xs} className="text-aqua" />}
                        </span>
                      </td>
                      <td
                        className={`py-2.5 px-3 text-center text-green-600 cursor-pointer transition-colors ${isExpanded && expandCol === 'done' ? 'bg-aqua-glow/20 font-bold' : 'hover:bg-aqua-glow/10'}`}
                        onClick={(e) => { e.stopPropagation(); handleCellClick(i, 'done'); }}
                      >
                        <span className="inline-flex items-center gap-1">
                          {o.completed}
                          {isExpanded && expandCol === 'done' && <ChevronDown size={ICON_PX.xs} className="text-aqua" />}
                        </span>
                      </td>
                      <td
                        className={`py-2.5 px-3 text-center cursor-pointer transition-colors ${isExpanded && expandCol === 'overdue' ? 'bg-aqua-glow/20 font-bold' : 'hover:bg-aqua-glow/10'}`}
                        onClick={(e) => { e.stopPropagation(); handleCellClick(i, 'overdue'); }}
                      >
                        <span className="inline-flex items-center gap-1">
                          {o.overdue > 0 ? <span className="text-red-critical">{o.overdue}</span> : '0'}
                          {isExpanded && expandCol === 'overdue' && <ChevronDown size={ICON_PX.xs} className="text-aqua" />}
                        </span>
                      </td>
                    </tr>
                    {/* ── Accordion expand row ── */}
                    {isExpanded && (
                      <tr ref={expandRef}>
                        <td colSpan={5} className="p-0">
                          <div className="bg-surface-warm px-6 py-3 border-b border-nn-border">
                            <p className="text-caption text-text-muted mb-2">
                              Showing {expandedTasks.length} {colLabel} task{expandedTasks.length !== 1 ? 's' : ''} for {o.individual || o.role.replace(/_/g, ' ')}
                            </p>
                            {expandedTasks.length === 0 ? (
                              <p className="text-body-sm text-text-muted py-3 text-center">No tasks in this category.</p>
                            ) : (
                              <div className="space-y-1.5">
                                {expandedTasks.map((task) => (
                                  <div
                                    key={task.id}
                                    className="flex items-center gap-3 rounded-lg border border-nn-border bg-nn-white px-3 py-2.5"
                                  >
                                    <DomainDot domain={task.domain} />
                                    <div className="flex-1 min-w-0">
                                      <span className="text-body-sm font-medium text-text-dark truncate block">{task.taskName}</span>
                                      <span className="text-caption text-text-muted">{task.dueDate} {task.dueTime} · {task.cadence}</span>
                                    </div>
                                    <StatusBadge status={task.status === 'overdue' ? 'critical' : task.status === 'completed' ? 'on-track' : 'monitoring'}>
                                      {task.status === 'overdue' ? 'Overdue' : task.status === 'completed' ? 'Done' : 'Pending'}
                                    </StatusBadge>
                                    {/* Action pill group */}
                                    <div className="flex items-center gap-1 shrink-0 p-0.5" style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid var(--surface)', borderRadius: 7 }}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button type="button" onClick={() => handleSendEmail(task, o.individual)} className="flex items-center p-1 rounded hover:bg-surface-warm transition-colors" style={{ color: 'var(--royal)' }}>
                                            <Bell size={ICON_PX.md} />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom">Send reminder</TooltipContent>
                                      </Tooltip>
                                      {/* Re-assign dropdown directly from icon */}
                                      <DropdownMenu>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <DropdownMenuTrigger asChild>
                                              <button type="button" className="flex items-center p-1 rounded hover:bg-surface-warm transition-colors" style={{ color: 'var(--aqua)' }}>
                                                <UserPlus size={ICON_PX.md} />
                                              </button>
                                            </DropdownMenuTrigger>
                                          </TooltipTrigger>
                                          <TooltipContent side="bottom">Re-assign</TooltipContent>
                                        </Tooltip>
                                        <DropdownMenuContent align="end" className="min-w-[160px]">
                                          <DropdownMenuLabel className="text-caption">Assign to</DropdownMenuLabel>
                                          <DropdownMenuRadioGroup value="" onValueChange={(v) => handleReassign(task.id, v)}>
                                            {getReassignOptions(o.role).map((opt) => (
                                              <DropdownMenuRadioItem key={opt.value} value={opt.value} className="text-body-sm">
                                                {opt.label}
                                              </DropdownMenuRadioItem>
                                            ))}
                                          </DropdownMenuRadioGroup>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                      {task.status !== 'completed' && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <button type="button" onClick={() => handleMarkComplete(task)} className="flex items-center p-1 rounded hover:bg-surface-warm transition-colors" style={{ color: 'var(--green)' }}>
                                              <CheckCircle2 size={ICON_PX.md} />
                                            </button>
                                          </TooltipTrigger>
                                          <TooltipContent side="bottom">Mark complete</TooltipContent>
                                        </Tooltip>
                                      )}
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button type="button" onClick={() => handleCancel(task)} className="flex items-center p-1 rounded hover:bg-surface-warm transition-colors" style={{ color: 'var(--red-critical)' }}>
                                            <XCircle size={ICON_PX.md} />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom">Cancel task</TooltipContent>
                                      </Tooltip>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modals ── */}
      <CompletionModal
        open={completeOpen}
        onOpenChange={setCompleteOpen}
        task={modalTask}
        onComplete={onCompleteConfirm}
      />
      <SkipReasonModal
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        task={modalTask}
        onSkip={onCancelConfirm}
      />
    </div>
  );
}

export default PMOverviewTab;
